import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";

const CartContext = createContext(null);
const KEY = "rf-cart-v1";

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem(KEY)) || [];
    } catch {
      return [];
    }
  });
  const [open, setOpen] = useState(false);

  useEffect(() => {
    localStorage.setItem(KEY, JSON.stringify(items));
  }, [items]);

  const itemKey = (x) => `${x.slug}|${x.size || ""}|${x.variant || ""}`;

  const add = (item) => {
    setItems((prev) => {
      const i = prev.findIndex((x) => itemKey(x) === itemKey(item));
      if (i > -1) {
        const next = [...prev];
        next[i] = { ...next[i], qty: Math.min(next[i].qty + (item.qty || 1), 10) };
        return next;
      }
      return [...prev, { ...item, qty: item.qty || 1 }];
    });
    const label = [item.variant, item.size ? `SIZE ${item.size}` : ""].filter(Boolean).join(" — ");
    toast.success(`${item.name}${label ? " — " + label : ""} ADDED TO BAG`);
  };

  const updateQty = (slug, size, variant, qty) => {
    setItems((prev) =>
      prev
        .map((x) =>
          itemKey(x) === `${slug}|${size || ""}|${variant || ""}`
            ? { ...x, qty: Math.max(1, Math.min(qty, 10)) }
            : x
        )
        .filter((x) => x.qty > 0)
    );
  };

  const remove = (slug, size, variant) => {
    setItems((prev) => prev.filter((x) => itemKey(x) !== `${slug}|${size || ""}|${variant || ""}`));
  };

  const clear = () => setItems([]);

  const count = useMemo(() => items.reduce((n, x) => n + x.qty, 0), [items]);
  const subtotal = useMemo(() => items.reduce((n, x) => n + x.qty * x.price, 0), [items]);

  return (
    <CartContext.Provider
      value={{ items, add, updateQty, remove, clear, count, subtotal, open, setOpen }}
    >
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
